import { IconButton } from '@repo/foundations/components/Buttons';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { SentimentType } from '../../../components/AIAssistant/AIFeedbackModal/constants';
import { MessageAIError } from '../../../components/AIAssistant/Messages/MessageAIError';
import { MessageAILoading } from '../../../components/AIAssistant/Messages/MessageAILoading';
import { MessageContentV3 } from '../../../components/AIAssistant/Messages/MessageContentV3';
import type { QuestionStore } from '../store';
import { QuestionReplyBody } from './QuestionReplyBody';

export const AiReply = observer(
	({
		replyId,
		store,
		onClickPositiveFeedback,
		onClickNegativeFeedback,
	}: {
		replyId: string;
		store: QuestionStore;
		onClickPositiveFeedback?: () => void;
		onClickNegativeFeedback?: () => void;
	}) => {
		const reply = store.replies.get(replyId)!;

		const messages = reply.reply.ai_structured_response ?? [];

		const shouldDisplayLoading =
			reply.reply.ai_status === 'pending' ||
			(reply.reply.ai_status === 'running' && !messages.length);
		const shouldDisplayFailure = reply.reply.ai_status === 'failed';

		const additionalToolbarButtons = useMemo(() => {
			const isPositiveFeedbackSubmitted =
				reply.reply.embedded_prompt?.user_feedback_raw?.sentiment ===
				SentimentType.Positive;
			const isNegativeFeedbackSubmitted =
				reply.reply.embedded_prompt?.user_feedback_raw?.sentiment ===
				SentimentType.Negative;

			return (
				<>
					{onClickPositiveFeedback &&
						(isPositiveFeedbackSubmitted ? (
							<IconButton
								iconName="thumbUpFilled"
								variant="tertiary"
								onClick={onClickPositiveFeedback}
								tooltip="Positive feedback for this prompt already submitted"
								className="show-on-hover"
							/>
						) : (
							<IconButton
								iconName="thumbUp"
								variant="tertiary"
								onClick={onClickPositiveFeedback}
								tooltip="Good response"
								className="show-on-hover"
							/>
						))}
					{onClickNegativeFeedback &&
						(isNegativeFeedbackSubmitted ? (
							<IconButton
								iconName="thumbDownFilled"
								variant="tertiary"
								onClick={onClickNegativeFeedback}
								tooltip="Negative feedback for this prompt already submitted"
								className="show-on-hover"
							/>
						) : (
							<IconButton
								iconName="thumbDown"
								variant="tertiary"
								onClick={onClickNegativeFeedback}
								tooltip="Bad response"
								className="show-on-hover"
							/>
						))}
				</>
			);
		}, [
			onClickPositiveFeedback,
			onClickNegativeFeedback,
			reply.reply.embedded_prompt?.user_feedback_raw?.sentiment,
		]);

		return (
			<QuestionReplyBody
				replyId={replyId}
				store={store}
				additionalToolbarButtons={additionalToolbarButtons}
			>
				{shouldDisplayLoading && (
					<MessageAILoading steps={reply.reply.ai_steps ?? []} />
				)}
				{shouldDisplayFailure && <MessageAIError />}
				{['running', 'completed', 'stopped'].includes(
					reply.reply.ai_status ?? ''
				) && (
					<MessageContentV3
						messages={messages}
						isRunning={reply.reply.ai_status === 'running'}
					/>
				)}
			</QuestionReplyBody>
		);
	}
);
